import { useState } from "react";
import { motion, HTMLMotionProps } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { cn, pct } from "../lib/utils";

/* ----------------------------------------------------------------- Card ---- */
export function Card({
  className,
  children,
  hover = false,
  ...rest
}: HTMLMotionProps<"div"> & { hover?: boolean }) {
  return (
    <motion.div
      className={cn(
        "card-surface relative overflow-hidden rounded-2xl",
        hover && "transition-shadow duration-300 hover:shadow-[var(--shadow-lg)]",
        className,
      )}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* ----------------------------------------------------------- TrendChip ---- */
/** `cap`: a change bigger than this reads "500%+" instead of the exact number. */
export function TrendChip({ value, positive, cap }: { value: number; positive: boolean; cap?: number }) {
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular",
        positive
          ? "bg-[var(--success-soft)] text-[var(--success)]"
          : "bg-[var(--danger-soft)] text-[var(--danger)]",
      )}
    >
      <Icon className="h-3 w-3" strokeWidth={2.5} />
      {cap && Math.abs(value) > cap ? `${cap}%+` : pct(Math.abs(value)).replace("+", "")}
    </span>
  );
}

/* --------------------------------------------------------------- Badge ---- */
export function Badge({
  children,
  tone = "neutral",
  className,
}: {
  children: React.ReactNode;
  tone?: "neutral" | "accent" | "success" | "warning" | "danger" | "legacy";
  className?: string;
}) {
  const tones: Record<string, string> = {
    neutral: "bg-[var(--surface-3)] text-muted border border-[var(--border)]",
    accent: "bg-[var(--accent-soft)] text-[var(--accent-bright)] border border-[color-mix(in_oklab,var(--accent)_35%,transparent)]",
    success: "bg-[var(--success-soft)] text-[var(--success)]",
    warning: "bg-[var(--warning-soft)] text-[var(--warning)]",
    danger: "bg-[var(--danger-soft)] text-[var(--danger)]",
    // Early-adopter plum — off the accent (upgrade CTAs), matching the
    // legacy banner / plan-card badges.
    legacy: "bg-[var(--legacy-soft)] text-[var(--legacy)] border border-[color-mix(in_oklab,var(--legacy)_35%,transparent)]",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide",
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/* --------------------------------------------------------- SectionTitle --- */
export function SectionTitle({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
        {subtitle && <p className="mt-0.5 text-[13px] text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/* --------------------------------------------------------------- Avatar --- */
/** LinkedIn CDN pictures (media.licdn.com) expire and refuse requests made
 *  outside a LinkedIn session, so they mostly render as broken images. */
const isExpiringPhoto = (src?: string) => !!src && /media\.licdn\.com/.test(src);

export function Avatar({
  label,
  src,
  size = 36,
  className,
}: {
  label: string;
  src?: string;
  size?: number;
  className?: string;
}) {
  // A18 (2026-09-21): a picture that fails to load, or a LinkedIn CDN link
  // that will, falls back to the initials tile instead of a blank circle.
  // The initials also show while the picture loads, which was an empty
  // circle for a few seconds (QA d22, p3, o7).
  // Remembers WHICH link loaded or failed, so a new link starts over with no
  // reset effect. The old reset ran in an effect after mount, and a photo
  // already in the browser cache (the viewer's own, shown in the top bar)
  // finished loading before it, so the reset hid it behind the initials
  // for good (owner report 2026-10-09).
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const loaded = !!src && loadedSrc === src;
  const failed = !!src && failedSrc === src;
  const showImg = !!src && !failed && !isExpiringPhoto(src);
  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[var(--surface-3)] to-[var(--surface)] text-[11px] font-semibold text-foreground ring-1 ring-[var(--border-strong)]",
        className,
      )}
      style={{ width: size, height: size }}
    >
      {!(showImg && loaded) && label}
      {showImg && (
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoadedSrc(src ?? null)}
          onError={() => setFailedSrc(src ?? null)}
          className={cn(
            "absolute inset-0 h-full w-full rounded-full object-cover",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </div>
  );
}

/* --------------------------------------------------------------- XLogo ---- */
/** The X (formerly Twitter) logo. lucide only has the old bird (QA s4). */
export function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
    </svg>
  );
}
